import type { ConsentPurpose } from '@/api/user/types/privacy-consents';
import logger from '@/services/logger';
import {
  isMetaConsentOffered,
  resolveMetaConsentOffer,
  setMetaAccount,
  setMetaConsent,
} from '@/services/meta-app-events';
import { isSessionReplayConsentOffered, stopSessionReplay } from '@/services/session-replay';
import {
  CONSENT_PROMPT_VERSION,
  getConsentChoice,
  getPrivacyConsents,
  needsConsentPrompt,
  savePrivacyConsents,
} from '@/services/privacy-consents';

/**
 * The user's choices on the consent sheet and in Settings. Each purpose is stored separately
 * by the backend; the side effects of a purpose only follow a saved choice, except a
 * revocation, which takes effect immediately.
 */

export { isSessionReplayConsentOffered };

/** Purposes this user may choose, in display order. */
export const offeredConsentPurposes = (dateOfBirth?: string | null): ConsentPurpose[] => [
  ...(isMetaConsentOffered(dateOfBirth) ? (['metaAdMeasurement'] as const) : []),
  ...(isSessionReplayConsentOffered(dateOfBirth) ? (['sessionReplay'] as const) : []),
];

/** Show the consent sheet: the account's consents are loaded, it has not answered the current prompt, and something is offered. */
export const shouldShowConsentPrompt = (accountId: string | null | undefined, dateOfBirth?: string | null) =>
  needsConsentPrompt(getPrivacyConsents(accountId)) && offeredConsentPurposes(dateOfBirth).length > 0;

/**
 * The consent sheet's Continue: stores an explicit choice for every offered purpose (anything
 * not switched on is a denial) and marks the prompt answered. Returns false when it could not be
 * saved; nothing is granted then and the prompt is shown again later.
 */
export const submitConsentPrompt = async (
  accountId: string,
  dateOfBirth: string | null | undefined,
  choices: Partial<Record<ConsentPurpose, boolean>>,
  source: 'onboarding' | 'prompt'
): Promise<boolean> => {
  const offered = offeredConsentPurposes(dateOfBirth);
  if (!offered.length) return false;
  // Leaving a granted Meta consent off revokes it: stop first, as in Settings.
  if (
    offered.includes('metaAdMeasurement') &&
    choices.metaAdMeasurement !== true &&
    getConsentChoice(accountId, 'metaAdMeasurement') === 'granted'
  ) {
    await setMetaConsent(false);
  }
  // Same for Session Replay: stop recording before saving.
  if (offered.includes('sessionReplay') && choices.sessionReplay !== true) stopSessionReplay();
  try {
    const saved = await savePrivacyConsents(
      accountId,
      Object.fromEntries(offered.map(purpose => [purpose, choices[purpose] === true])),
      source,
      CONSENT_PROMPT_VERSION
    );
    if (!saved) return false;
  } catch {
    logger.info('[PrivacyConsents] saving the consent prompt failed', { source });
    return false;
  }
  if (offered.includes('metaAdMeasurement')) {
    // The birth date the offer was based on: the cached user may not have it yet (onboarding).
    await setMetaAccount({ id: accountId, profile: { dateOfBirth } });
    await resolveMetaConsentOffer(getConsentChoice(accountId, 'metaAdMeasurement') === 'granted');
  }
  return true;
};

/**
 * A Settings toggle. Throws when the choice could not be saved; a revocation then stays in force
 * on this device, also across restarts, until it is saved (see services/privacy-consents).
 */
export const updateConsentSetting = async (accountId: string, purpose: ConsentPurpose, granted: boolean) => {
  // Revoking stops Meta transmission / Replay recording before anything else, even if saving
  // then fails (the pending revocation keeps it stopped, also after a restart).
  if (purpose === 'sessionReplay' && !granted) stopSessionReplay();
  if (purpose === 'metaAdMeasurement' && !granted) await setMetaConsent(false);
  const saved = await savePrivacyConsents(accountId, { [purpose]: granted }, 'settings');
  if (purpose === 'metaAdMeasurement' && granted && saved?.metaAdMeasurement.granted) await setMetaConsent(true);
  return saved;
};
